import { MutableRefObject } from 'react'
import {
  AudioNotificationSound,
  GeneratedNotificationSound,
  getNotificationSound,
  isAudioNotificationSound,
} from './sound-options'
import { NotificationSoundKey } from './types'

export interface NotificationSoundPlayback {
  duration: number
  stop: () => void
}

const audioBufferCache = new Map<string, Promise<AudioBuffer>>()

export const initNotificationAudioContext = (ref: MutableRefObject<AudioContext | null>): void => {
  if (ref.current) {
    ref.current.resume().catch(() => undefined)
    return
  }

  const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext
  if (!AudioContextConstructor) {
    return
  }

  ref.current = new AudioContextConstructor()
  ref.current.resume().catch(() => undefined)
}

const loadAudioBuffer = (context: AudioContext, audioUrl: string): Promise<AudioBuffer> => {
  const cached = audioBufferCache.get(audioUrl)
  if (cached) {
    return cached
  }

  const loading = fetch(audioUrl)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load notification sound: ${response.status}`)
      }
      return response.arrayBuffer()
    })
    .then(buffer => context.decodeAudioData(buffer))
    .catch(error => {
      audioBufferCache.delete(audioUrl)
      throw error
    })

  audioBufferCache.set(audioUrl, loading)
  return loading
}

const createOutput = (context: AudioContext, gainValue: number) => {
  const input = context.createGain()
  const compressor = context.createDynamicsCompressor()
  let disconnected = false

  input.gain.setValueAtTime(gainValue, context.currentTime)
  compressor.threshold.setValueAtTime(-1, context.currentTime)
  compressor.knee.setValueAtTime(0, context.currentTime)
  compressor.ratio.setValueAtTime(20, context.currentTime)
  compressor.attack.setValueAtTime(0.001, context.currentTime)
  compressor.release.setValueAtTime(0.12, context.currentTime)

  input.connect(compressor)
  compressor.connect(context.destination)

  return {
    input,
    disconnect: () => {
      if (disconnected) {
        return
      }
      disconnected = true
      input.disconnect()
      compressor.disconnect()
    },
  }
}

const playGeneratedSound = (
  context: AudioContext,
  sound: GeneratedNotificationSound,
): NotificationSoundPlayback => {
  const startsAt = context.currentTime + 0.02
  const output = createOutput(context, sound.gain)
  const oscillators: OscillatorNode[] = []
  const duration = Math.max(...sound.tones.map(tone => tone.at + tone.duration))
  let stopped = false

  sound.tones.forEach(tone => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    const toneStartsAt = startsAt + tone.at
    const toneEndsAt = toneStartsAt + tone.duration
    const attackEndsAt = Math.min(toneStartsAt + 0.015, toneEndsAt)

    oscillator.type = tone.wave || 'sine'
    oscillator.frequency.setValueAtTime(tone.frequency, toneStartsAt)
    if (tone.endFrequency) {
      oscillator.frequency.exponentialRampToValueAtTime(tone.endFrequency, toneEndsAt)
    }

    gain.gain.setValueAtTime(0.0001, toneStartsAt)
    gain.gain.exponentialRampToValueAtTime(Math.max(tone.volume, 0.0001), attackEndsAt)
    gain.gain.exponentialRampToValueAtTime(0.0001, toneEndsAt)

    oscillator.connect(gain)
    gain.connect(output.input)
    oscillator.start(toneStartsAt)
    oscillator.stop(toneEndsAt + 0.02)
    oscillators.push(oscillator)
  })

  const cleanupTimer = window.setTimeout(() => {
    stopped = true
    output.disconnect()
  }, (duration + 0.12) * 1000)
  const stop = () => {
    if (stopped) {
      return
    }
    stopped = true
    window.clearTimeout(cleanupTimer)
    const now = context.currentTime
    output.input.gain.cancelScheduledValues(now)
    output.input.gain.setValueAtTime(Math.max(output.input.gain.value, 0.0001), now)
    output.input.gain.exponentialRampToValueAtTime(0.0001, now + 0.03)
    oscillators.forEach(oscillator => {
      try {
        oscillator.stop(now + 0.04)
      } catch (_error) {
        // The oscillator may have already finished naturally.
      }
    })
    window.setTimeout(output.disconnect, 60)
  }

  return { duration, stop }
}

const playAudioSound = async (
  context: AudioContext,
  sound: AudioNotificationSound,
): Promise<NotificationSoundPlayback> => {
  const buffer = await loadAudioBuffer(context, sound.audioUrl)
  const source = context.createBufferSource()
  const output = createOutput(context, sound.gain)
  let stopped = false

  source.buffer = buffer
  source.connect(output.input)
  source.start()

  const cleanup = () => {
    if (stopped) {
      return
    }
    stopped = true
    source.disconnect()
    output.disconnect()
  }
  source.addEventListener('ended', cleanup, { once: true })

  const stop = () => {
    if (stopped) {
      return
    }
    try {
      source.stop()
    } catch (_error) {
      // The source may have already finished naturally.
    }
    cleanup()
  }

  return { duration: buffer.duration, stop }
}

export const preloadNotificationSound = async (
  ref: MutableRefObject<AudioContext | null>,
  key: NotificationSoundKey,
): Promise<void> => {
  const context = ref.current
  const sound = getNotificationSound(key)
  if (!context || !isAudioNotificationSound(sound)) {
    return
  }

  await loadAudioBuffer(context, sound.audioUrl)
}

export const playNotificationSound = async (
  ref: MutableRefObject<AudioContext | null>,
  key: NotificationSoundKey,
): Promise<NotificationSoundPlayback | null> => {
  const context = ref.current
  if (!context) {
    return null
  }

  await context.resume()
  const sound = getNotificationSound(key)
  return isAudioNotificationSound(sound)
    ? playAudioSound(context, sound)
    : playGeneratedSound(context, sound)
}
